import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { connect } from 'react-redux';
import checkUiRights from '../../helpers/checkUiRights';
import { selectUiRights, selectUiRightsLoading } from '../../redux/authentication/selectors';

/**
 * Functional component that checks user rights and conditionally renders a component or redirects.
 * @property {React.Component} component - The component to be rendered if access is granted.
 * @property {object} uiRights - User's rights, fetched from the Redux store.
 * @property {Array} accessRights - Array of rights required to access the route.
 * @param {object} rest - Any additional props passed to the component.
 * @returns {JSX.Element|null} - Returns the component if access is allowed, otherwise redirects to the not-allowed page.
 */
const AuthorizedRouteComponent = ({ component: Component, uiRights, uiRightsLoading, accessRights, ...rest }) => {
  const navigate = useNavigate();

  // Memoize the result of checkUiRights to avoid redundant computations
  const isAuthorized = useMemo(() => checkUiRights(uiRights, accessRights), [uiRights, accessRights]);

  useEffect(() => {
    if (!uiRightsLoading && uiRights && !isAuthorized) {
      navigate('/notAllowed');
    }
  }, [isAuthorized, uiRightsLoading, uiRights, navigate]);

  if (uiRightsLoading || !uiRights) {
    return null;
  }

  return isAuthorized ? <Component {...rest} /> : null;
};

export const AuthorizedRoute = connect((state) => ({
  uiRights: selectUiRights(state),
  uiRightsLoading: selectUiRightsLoading(state)
}))(AuthorizedRouteComponent);
